/**
 * Horisontal verktøylinje over canvaset.
 * Visningsbrytere og utvalgs-handlinger med synlige tekst-etiketter,
 * pluss «?»-knapp med hurtigtast-hjelp (flyttet ut av canvas-overlayet).
 */

import React, { useState } from 'react';
import {
  Grid3X3,
  Tag,
  Flame,
  Radio,
  RotateCcw,
  Trash2,
  AlignStartVertical,
  HelpCircle,
  Eraser,
} from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { createEmptyLayout } from '../utils/layoutStorage';

/** Toggle-knapp med ikon + tekst og tydelig aktiv-tilstand */
const ToolbarToggle: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
}> = ({ active, onClick, icon, label, tooltip }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
      active
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
    title={tooltip}
    aria-pressed={active}
  >
    {icon}
    {label}
  </button>
);

/** Handlingsknapp med ikon + tekst (nøytral eller destruktiv variant) */
const ToolbarAction: React.FC<{
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  tooltip: string;
  destructive?: boolean;
}> = ({ onClick, icon, label, tooltip, destructive }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
      destructive
        ? 'text-red-600 hover:bg-red-50'
        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    }`}
    title={tooltip}
  >
    {icon}
    {label}
  </button>
);

const EditorToolbar: React.FC = () => {
  const { state, dispatch, warehouseId } = useLayoutEditor();
  const { t } = useTranslation();
  const { editorState } = state;
  const [showHelp, setShowHelp] = useState(false);

  const hasSelection = editorState.selectedRacks.size > 0;

  const handleResetLayout = () => {
    if (window.confirm(t('manager.modules.warehouseLayout.toolbar.resetConfirm', {}, 'Er du sikker på at du vil nullstille layouten? Alle reolplasseringer vil bli fjernet.'))) {
      dispatch({ type: 'RESET_LAYOUT', emptyLayout: createEmptyLayout(warehouseId, state.layout?.name) });
    }
  };

  // Hurtigtast-oversikt: [tast-etikett, beskrivelse]
  const shortcuts: Array<[string, string]> = [
    [t('manager.modules.warehouseLayout.toolbar.helpKeyMulti', {}, 'Ctrl + klikk'), t('manager.modules.warehouseLayout.toolbar.helpMulti', {}, 'Legg til / fjern i utvalg')],
    [t('manager.modules.warehouseLayout.toolbar.helpKeyRange', {}, 'Shift + klikk'), t('manager.modules.warehouseLayout.toolbar.helpRange', {}, 'Velg rekke av reoler')],
    [t('manager.modules.warehouseLayout.toolbar.helpKeyRubber', {}, 'Dra i tomt område'), t('manager.modules.warehouseLayout.toolbar.helpRubber', {}, 'Områdevalg')],
    [t('manager.modules.warehouseLayout.toolbar.helpKeyPan', {}, 'Høyreklikk + dra'), t('manager.modules.warehouseLayout.toolbar.helpPan', {}, 'Panorér visningen')],
    ['R', t('manager.modules.warehouseLayout.toolbar.helpRotate', {}, 'Roter valgte')],
    ['G', t('manager.modules.warehouseLayout.toolbar.helpAlign', {}, 'Juster valgte til rutenett')],
    ['Delete', t('manager.modules.warehouseLayout.toolbar.helpDelete', {}, 'Slett valgte')],
    [t('manager.modules.warehouseLayout.toolbar.helpKeyNudge', {}, 'Piltaster'), t('manager.modules.warehouseLayout.toolbar.helpNudge', {}, 'Flytt valgte ett rutenett-steg')],
    [t('manager.modules.warehouseLayout.toolbar.helpKeyZoom', {}, '+ / − / musehjul'), t('manager.modules.warehouseLayout.toolbar.helpZoom', {}, 'Zoom inn og ut')],
    ['H', t('manager.modules.warehouseLayout.toolbar.helpResetView', {}, 'Tilbakestill zoom og posisjon')],
    ['Ctrl+Z / Ctrl+Shift+Z', t('manager.modules.warehouseLayout.toolbar.helpUndo', {}, 'Angre / gjenta')],
    ['Esc', t('manager.modules.warehouseLayout.toolbar.helpEsc', {}, 'Fjern utvalg')],
  ];

  return (
    <div className="relative flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded-lg shadow-sm mb-2 flex-shrink-0 flex-wrap">
      {/* Visningsbrytere */}
      <span className="hidden lg:inline text-xs font-semibold uppercase tracking-wide text-gray-400 px-1.5">
        {t('manager.modules.warehouseLayout.toolbar.groupView', {}, 'Visning')}
      </span>
      <ToolbarToggle
        active={editorState.showGrid}
        onClick={() => dispatch({ type: 'TOGGLE_EDITOR_OPTION', option: 'showGrid' })}
        icon={<Grid3X3 className="w-4 h-4" />}
        label={t('manager.modules.warehouseLayout.toolbar.labelGrid', {}, 'Rutenett')}
        tooltip={t('manager.modules.warehouseLayout.toolbar.showGrid', {}, 'Vis rutenett')}
      />
      <ToolbarToggle
        active={editorState.showLabels}
        onClick={() => dispatch({ type: 'TOGGLE_EDITOR_OPTION', option: 'showLabels' })}
        icon={<Tag className="w-4 h-4" />}
        label={t('manager.modules.warehouseLayout.toolbar.labelLabels', {}, 'Etiketter')}
        tooltip={t('manager.modules.warehouseLayout.toolbar.showLabels', {}, 'Vis etiketter')}
      />
      <ToolbarToggle
        active={editorState.showHeatmap}
        onClick={() => dispatch({ type: 'TOGGLE_EDITOR_OPTION', option: 'showHeatmap' })}
        icon={<Flame className="w-4 h-4" />}
        label={t('manager.modules.warehouseLayout.toolbar.labelHeatmap', {}, 'Heatmap')}
        tooltip={t('manager.modules.warehouseLayout.toolbar.showHeatmap', {}, 'Vis heatmap')}
      />
      <ToolbarToggle
        active={editorState.showActivity}
        onClick={() => dispatch({ type: 'TOGGLE_EDITOR_OPTION', option: 'showActivity' })}
        icon={<Radio className="w-4 h-4" />}
        label={t('manager.modules.warehouseLayout.toolbar.labelActivity', {}, 'Aktivitet')}
        tooltip={t('manager.modules.warehouseLayout.toolbar.showActivity', {}, 'Vis aktivitet')}
      />

      {/* Utvalgs-handlinger — vises kun når reoler er valgt */}
      {hasSelection && (
        <>
          <div className="w-px h-5 bg-gray-200 mx-1 self-center" />
          <ToolbarAction
            onClick={() => dispatch({ type: 'ROTATE_SELECTED' })}
            icon={<RotateCcw className="w-4 h-4" />}
            label={t('manager.modules.warehouseLayout.toolbar.labelRotate', {}, 'Roter')}
            tooltip={t('manager.modules.warehouseLayout.toolbar.rotateSelected', {}, 'Roter valgte (R)')}
          />
          <ToolbarAction
            onClick={() => dispatch({ type: 'ALIGN_SELECTED_TO_GRID' })}
            icon={<AlignStartVertical className="w-4 h-4" />}
            label={t('manager.modules.warehouseLayout.toolbar.labelAlign', {}, 'Juster')}
            tooltip={t('manager.modules.warehouseLayout.toolbar.alignSelected', {}, 'Juster valgte til rutenett (G)')}
          />
          <ToolbarAction
            onClick={() => dispatch({ type: 'DELETE_SELECTED' })}
            icon={<Trash2 className="w-4 h-4" />}
            label={t('manager.modules.warehouseLayout.toolbar.labelDelete', {}, 'Slett')}
            tooltip={t('manager.modules.warehouseLayout.toolbar.deleteSelected', {}, 'Slett valgte (Delete)')}
            destructive
          />
        </>
      )}

      <div className="w-px h-5 bg-gray-200 mx-1 self-center" />
      <ToolbarAction
        onClick={handleResetLayout}
        icon={<Eraser className="w-4 h-4" />}
        label={t('manager.modules.warehouseLayout.toolbar.labelReset', {}, 'Nullstill')}
        tooltip={t('manager.modules.warehouseLayout.toolbar.resetLayout', {}, 'Nullstill layout')}
        destructive
      />

      {/* Hjelp-popover med hurtigtaster */}
      <div className="ml-auto">
        <button
          onClick={() => setShowHelp(v => !v)}
          className={`p-1.5 rounded-md transition-colors ${
            showHelp ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          title={t('manager.modules.warehouseLayout.toolbar.helpButton', {}, 'Hurtigtaster og mus')}
          aria-expanded={showHelp}
        >
          <HelpCircle className="w-4 h-4" />
        </button>
      </div>

      {showHelp && (
        <>
          {/* Klikk-fanger for å lukke popoveren */}
          <div className="fixed inset-0 z-20" onClick={() => setShowHelp(false)} />
          <div className="absolute right-0 top-full mt-1 z-30 w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">
              {t('manager.modules.warehouseLayout.toolbar.helpTitle', {}, 'Hurtigtaster og mus')}
            </h4>
            <div className="space-y-1">
              {shortcuts.map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between gap-3 text-xs">
                  <span className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-gray-700 whitespace-nowrap">
                    {key}
                  </span>
                  <span className="text-gray-600 text-right">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EditorToolbar;
